import dayjs from 'dayjs'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ChevronLeftIcon, ChevronRightIcon} from '@/components/ui/icon/Icon'
import {
    StyledCalendarHeader,
    StyledCalendarWrapper,
    StyledDayButton,
    StyledDayGrid,
    StyledTodayIndicator,
    StyledWeekDayContainer
} from '@/features/agenda/components/agenda-sidebar-calendar/style'
import {useTranslation} from '@/translations/i18n'
import {capitalize} from '@/utilities/helpers'
import {DPCalendar, DPDay, DPOffsetValue, DPPropGetter, DPPropsGetterConfig, useDatePicker} from '@rehookify/datepicker'
import {useURLParsedParams} from '@/hooks/useURLParsedParams'
import {AgendaUrlParams} from '@/features/agenda/types'
import {isToday} from '@/features/agenda/utils'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'

type DateSelectorProps = {
    calendar: DPCalendar
    addOffset?: (offsetValue: DPOffsetValue, config?: DPPropsGetterConfig) => DPPropGetter
    subtractOffset?: (offsetValue: DPOffsetValue, config?: DPPropsGetterConfig) => DPPropGetter
    dayButton: (day: DPDay) => DPPropGetter
    weekDays: string[]
    locale?: string
}

export const AgendaSidebarCalendar = () => {
    const urlParams = useURLParsedParams(AgendaUrlParams)
    const {navigateToDate} = useAgendaNavigation()
    const selectedDate =
        urlParams.success && urlParams.data.date ? dayjs(urlParams.data.date, 'DD-MM-YYYY').toDate() : dayjs().toDate()

    const {
        data: {weekDays, calendars},
        propGetters: {dayButton, addOffset, subtractOffset}
    } = useDatePicker({
        selectedDates: [selectedDate],
        dates: {toggle: true, mode: 'single'},
        calendar: {
            startDay: 1,
            offsets: [...Array(1).keys()].slice(1)
        },
        onDatesChange: (dates: Date[]) => {
            if (urlParams.success) {
                navigateToDate(dates[0])
            }
        }
    })

    // Custom day button to disable Sundays
    const customDayButton = (day: DPDay) => {
        const props = dayButton(day)
        // Check if the day is Sunday (0)
        const isSunday = day.$date.getDay() === 0

        return {
            ...props,
            disabled: isSunday || props.disabled
        }
    }

    return (
        <Flexbox fullWidth>
            {calendars.map((calendar, index) => (
                <AgendaSidebarDateSelector
                    key={index}
                    calendar={calendar}
                    addOffset={addOffset}
                    subtractOffset={subtractOffset}
                    dayButton={customDayButton}
                    weekDays={weekDays}
                />
            ))}
        </Flexbox>
    )
}

export const AgendaSidebarDateSelector = ({
    calendar,
    addOffset,
    subtractOffset,
    dayButton,
    weekDays
}: DateSelectorProps) => {
    const {
        i18n: {language}
    } = useTranslation()

    return (
        <StyledCalendarWrapper>
            <StyledCalendarHeader>
                <h3>
                    {capitalize(dayjs(`1-${calendar.month}-${calendar.year}`).locale(language).format('MMMM'))}{' '}
                    {dayjs(calendar.year).format('YYYY')}
                </h3>
                <Flexbox gap={2}>
                    <Button shape="square" variant="ghost" size="sm" {...subtractOffset?.({months: 1})}>
                        <ChevronLeftIcon size={20} />
                    </Button>
                    <Button shape="square" variant="ghost" size="sm" {...addOffset?.({months: 1})}>
                        <ChevronRightIcon size={20} />
                    </Button>
                </Flexbox>
            </StyledCalendarHeader>
            <StyledDayGrid>
                {weekDays.map((_, index) => (
                    <StyledWeekDayContainer key={index}>
                        {capitalize(
                            dayjs()
                                .locale(language)
                                .day(index + 1 <= 6 ? index + 1 : 0)
                                .format('dd')[0]
                                .slice(0, 2)
                        )}
                    </StyledWeekDayContainer>
                ))}
            </StyledDayGrid>
            <StyledDayGrid>
                {calendar.days.map((day, index) => {
                    const isSunday = day.$date.getDay() === 0
                    return (
                        <StyledDayButton
                            key={index}
                            $isInCurrentMonth={day.inCurrentMonth}
                            $isSelected={day.selected}
                            {...dayButton(day)}
                            disabled={isSunday || day.disabled}
                        >
                            {day.$date.getDate()}
                            {isToday(day.$date) && <StyledTodayIndicator $isSelected={day.selected} />}
                        </StyledDayButton>
                    )
                })}
            </StyledDayGrid>
        </StyledCalendarWrapper>
    )
}
